import { useEffect } from "react";
import { useAuthentication } from "@aviv-unified-frontend/runtime/authentication";

import { useOverlayOrchestrator } from "../../createOverlayOrchestratorContext";
import { useOverlayStepsOrchestrator } from "../../useOverlayStepsOrchestrator";
import { useShouldInitOverlay } from "../../useShouldInitOverlay";
import { BRAND_CONFIGURATION } from "../../../../../configs/brands";

import { KEY } from "./constants";

/**
 * Ensures that the Google One Tap root element has the attribute
 * `data-react-aria-top-layer="true"` so that React Aria can
 * properly manage focus for overlays.
 * See https://github.com/adobe/react-spectrum/issues/8784#issuecomment-3415368636
 */
const useGoogleOneTapAttribute = ({ active }: { active: boolean }) => {
  useEffect(() => {
    if (!active) return;

    let observer: MutationObserver | null = null;

    const applyAttribute = () => {
      const el = document.querySelector("#credential_picker_iframe");

      if (el) {
        el.setAttribute("data-react-aria-top-layer", "true");
        el.removeAttribute("inert");

        return true;
      }

      return false;
    };

    if (applyAttribute()) return;

    observer = new MutationObserver(() => {
      applyAttribute() && observer?.disconnect();
    });

    observer.observe(document.body, {
      childList: true,
      subtree: false,
    });

    return () => observer?.disconnect();
  }, [active]);
};

const { googleOneTap } = BRAND_CONFIGURATION.authentication ?? {};

export const useInitGoogleOneTap = () => {
  const { dispatch } = useOverlayOrchestrator();
  const canProceed = useOverlayStepsOrchestrator(KEY);
  const shouldInit = useShouldInitOverlay(KEY);
  const { state } = useAuthentication();

  useGoogleOneTapAttribute({ active: shouldInit });

  const isPending = state === "pending";

  const isActive = Boolean(
    googleOneTap && ["unauthenticated", "error"].includes(state),
  );

  useEffect(() => {
    if (!canProceed || isPending) return;

    const type = isActive ? "INIT" : "DISABLE";

    dispatch({ type, payload: KEY });
  }, [canProceed, isActive, isPending, dispatch]);
};
